import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/dist/query'
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'

import { apiSlice as BookkeepingApiSlice } from '@scouts/bookkeeping'

import autonomousRegions from '@/reducers/autonomous-regions'
import bankAccount from '@/reducers/bank-account'
import bankAccountIncomes from '@/reducers/bank-account-incomes'
import capitalGainsSummary from '@/reducers/capital-gains-summary'
import capitalGainsTransactions from '@/reducers/capital-gains-transactions'
import countries from '@/reducers/countries'
import deductionSummary from '@/reducers/deduction-summary'
import discounts from '@/reducers/discounts'
import dividends from '@/reducers/dividends'
import dividendsAndInterestSummary from '@/reducers/dividends-and-interest-summary'
import donationSummary from '@/reducers/donation-summary'
import donations from '@/reducers/donations'
import employmentSummary from '@/reducers/employment-summary'
import foreignIncome from '@/reducers/foreign-income'
import generalSummary from '@/reducers/general-summary'
import habitualResidence from '@/reducers/habitual-residence'
import messages from '@/reducers/messages'
import olderDependents from '@/reducers/older-dependents'
import otherIncome from '@/reducers/other-income'
import partner from '@/reducers/partner'
import pensionContributions from '@/reducers/pension-contributions'
import pensionContributionsSummary from '@/reducers/pension-contributions-summary'
import propertyIncomeSummary from '@/reducers/property-income-summary'
import propertyIncomes from '@/reducers/property-incomes'
import quarterlyTaxReturn from '@/reducers/quarterly-tax-return'
import quarterlyTaxReturnFiles from '@/reducers/quarterly-tax-return-files'
import quarterlyTaxReturnsAnnualSummary from '@/reducers/quarterly-tax-returns-annual-summary'
import quarterlyTaxReturnsAnnualSummaryFiles from '@/reducers/quarterly-tax-returns-annual-summary-files'
import referrals from '@/reducers/referrals'
import referredBy from '@/reducers/referred-by'
import request from '@/reducers/request'
import selfEmploymentActivities from '@/reducers/self-employment-activities'
import selfEmploymentProfile from '@/reducers/self-employment-profile'
import selfEmploymentSummary from '@/reducers/self-employment-summary'
import subscriptionPayments from '@/reducers/subscription-payments'
import taxOffice from '@/reducers/tax-office'
import taxReturn from '@/reducers/tax-return'
import taxReturnFiles from '@/reducers/tax-return-files'
import taxReturnPotentialDeductions from '@/reducers/tax-return-potential-deductions'
import testimonials from '@/reducers/testimonials'
import userFiles from '@/reducers/user-files'
import youngerDependents from '@/reducers/younger-dependents'
import addresses from '@/store/addresses/reducer'
import profile from '@/store/profile/reducer'
import quarterlyTaxReturns from '@/store/quarterly-tax-returns/reducer'
import socialSecurityProfile from '@/store/social-security-profile/reducer'
import userAgreements from '@/store/user-agreement/reducer'

import { apiSlice } from './api-slice'
import { apiRequestInterceptorMiddleware } from './middleware'

export const store = configureStore({
    reducer: {
        addresses,
        autonomousRegions,
        bankAccount,
        bankAccountIncomes,
        capitalGainsSummary,
        capitalGainsTransactions,
        countries,
        deductionSummary,
        discounts,
        dividends,
        dividendsAndInterestSummary,
        donations,
        donationSummary,
        employmentSummary,
        foreignIncome,
        generalSummary,
        habitualResidence,
        messages,
        olderDependents,
        otherIncome,
        partner,
        pensionContributions,
        pensionContributionsSummary,
        profile,
        propertyIncomes,
        propertyIncomeSummary,
        quarterlyTaxReturn,
        quarterlyTaxReturns,
        quarterlyTaxReturnFiles,
        quarterlyTaxReturnsAnnualSummary,
        quarterlyTaxReturnsAnnualSummaryFiles,
        referrals,
        referredBy,
        request,
        selfEmploymentActivities,
        selfEmploymentProfile,
        selfEmploymentSummary,
        socialSecurityProfile,
        subscriptionPayments,
        taxOffice,
        taxReturn,
        taxReturnPotentialDeductions,
        taxReturnFiles,
        testimonials,
        userFiles,
        userAgreements,
        youngerDependents,
        [apiSlice.reducerPath]: apiSlice.reducer,
        [BookkeepingApiSlice.reducerPath]: BookkeepingApiSlice.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            // TEC-722: Fix how we use Redux, so we don't have to silence warnings
            // Silences warnings about non-serializable values used in actions (e.g. onSuccess/onError functions in our case)
            serializableCheck: false,
            // Silences warnings about immutable values (e.g. saving native File objects in state)
            immutableCheck: false,
        })
            .concat(apiSlice.middleware)
            .concat(apiRequestInterceptorMiddleware)
            .concat(BookkeepingApiSlice.middleware),
})

setupListeners(store.dispatch)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
